import React, { useCallback, useEffect, useState } from "react";
import { EventContext, EventData } from "./EventContext";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/use-language";
import { usePenalty } from "@/hooks/use-penalty";
import { canSignupToEvent } from "@/types/penalty";
import { SupabaseEventsService } from "@/services/supabase-events";
import { useAuth } from "@/hooks/use-auth";
import { useSupabase } from "@/contexts/supabase";

function isUuid(value: string) {
  return /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(value);
}

function buildDummyParticipant(id: string, firstName: string, lastName?: string, phone?: string): { id: string; name: string; phone: string; groups: string[]; joinedAt?: Date } {
  const fullName = lastName ? `${firstName} ${lastName}` : firstName;
  return { id, name: fullName, phone: phone || "", groups: [], joinedAt: new Date() };
}

function overlayDummyTournamentData(data: EventData[]): EventData[] {
  // Only overlay if no participants present (i.e., fresh DB or RLS embeds disabled)
  const hasAnyParticipants = data.some(ev => (ev.participants || []).length > 0 || (ev.waitingList || []).length > 0);
  if (hasAnyParticipants) return data;

  const names: Array<[string, string]> = [
    ["henrik123", "Henrik Solberg"],
    ["anna456", "Anna Berg"],
    ["erik789", "Erik Carlsen"],
    ["sofia012", "Sofia Dahl"],
    ["magnus345", "Magnus Eriksen"],
    ["ingrid678", "Ingrid Foss"],
    ["ole901", "Ole Gundersen"],
    ["astrid234", "Astrid Hansen"],
    ["torstein567", "Torstein Iversen"],
    ["marie890", "Marie Jensen"],
    ["kristian123", "Kristian Knutsen"],
    ["linda456", "Linda Larsen"],
  ];
  const waitNames: Array<[string, string]> = [
    ["johanne789", "Johanne Moen"],
    ["andreas012", "Andreas Nord"],
    ["camilla345", "Camilla Olsen"],
  ];

  const buildRoster = (count: number, prefix: string): Array<[string, string]> => {
    const numericCount = Number.isFinite(count) ? count : 0;
    const safeCount = Math.max(0, numericCount);
    const roster: Array<[string, string]> = [];
    for (let i = 0; i < safeCount; i++) {
      if (i < names.length) {
        roster.push(names[i]);
      } else {
        roster.push([`${prefix}-${i + 1}`, generateName(i)]);
      }
    }
    return roster;
  };

  return data.map(ev => {
    const isMexicano = (ev.titleKey || ev.title || '').toLowerCase().includes('mexicano');
    const isTournament = ev.type === 'tournament';
    if (!isTournament) return ev;

    // For Mexicano: 11 participants (so user signup makes it 12)
    if (isMexicano) {
      const part = buildRoster((ev.maxParticipants ?? 0) - 1, `mex-${ev.id}`);
      return {
        ...ev,
        participants: part.map(([id]) => id),
        participantDetails: part.map(([id, n]) => buildDummyParticipant(id, n)),
        waitingList: [],
        waitingListDetails: [],
      };
    }

    // For other tournaments (Americano): full participants + waiting list
    const part = buildRoster(ev.maxParticipants ?? 0, `tournament-${ev.id}`);
    // Only show a waiting list if the event is effectively full
    const showWait = part.length >= ev.maxParticipants;
    const wl = showWait ? waitNames : [] as Array<[string, string]>;
    return {
      ...ev,
      participants: part.map(([id]) => id),
      participantDetails: part.map(([id, n]) => buildDummyParticipant(id, n)),
      waitingList: wl.map(([id]) => id),
      waitingListDetails: wl.map(([id, n]) => buildDummyParticipant(id, n)),
    };
  });
}

function isMexicanoEvent(ev: EventData) {
  return (ev.titleKey || ev.title || '').toLowerCase().includes('mexicano');
}

function nextMultipleOfFour(n: number) {
  if (n % 4 === 0) return n;
  return n + (4 - (n % 4));
}

function generateName(index: number) {
  const pool = [
    'Alex Johnson','Emma Wilson','Michael Brown','Sarah Davis','James Miller','Lisa Anderson',
    'David Taylor','Anna Martinez','Chris Thompson','Maria Garcia','Robert Lee','Jennifer White',
    'Daniel Harris','Jessica Clark','Matthew Lewis','Ashley Walker','Ryan Hall','Amanda Young',
    'Kevin King','Nicole Wright','Brandon Scott','Stephanie Green','Justin Adams','Rachel Baker'
  ];
  return pool[index % pool.length];
}

export function SupabaseEventProvider({ children }: { children: React.ReactNode }) {
  const { t } = useLanguage();
  const { addPenalty, getMemberPenalty } = usePenalty();
  const { user, isAuthenticated } = useAuth();
  const supabase = useSupabase();
  const [events, setEvents] = useState<EventData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signupHoursBeforeEvent, setSignupHoursBeforeEvent] = useState<number>(72);

  // Create the events service with the Clerk-authenticated client
  const eventsService = React.useMemo(() => new SupabaseEventsService(supabase), [supabase]);

  // Load signup window setting
  useEffect(() => {
    async function fetchSignupWindow() {
      const { data } = await supabase
        .from('system_settings')
        .select('signup_hours_before_event')
        .eq('id', 1)
        .single();
      
      if (data) {
        setSignupHoursBeforeEvent(data.signup_hours_before_event);
      }
    }
    if (isAuthenticated) {
      fetchSignupWindow();
    }
  }, [supabase, isAuthenticated]);

  // Dev helper: ensure minimum participants locally for smoother testing
  const ensureDevMinParticipants = useCallback((ev: EventData): EventData => ev, []);

  // Load events from Supabase
  const loadEvents = useCallback(async () => {
    if (!isAuthenticated) {
      setEvents([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      let eventData = await eventsService.getEvents();
      // Overlay dummy data for tournaments when DB doesn't have participants yet
      eventData = overlayDummyTournamentData(eventData);
      // Ensure the target event has at least 3 participants in dev
      eventData = eventData.map(ev => {
        if (ev.id === '750e8400-e29b-41d4-a716-446655440008' && ev.type === 'tournament') {
          const current = ev.participants?.length || 0;
          if (current < 3) {
            const base = [...(ev.participants || [])];
            const baseDetails = [...(ev.participantDetails || [])];
            for (let i = current; i < 3; i++) {
              const id = `dev-seed-${i + 1}`;
              base.push(id);
              baseDetails.push(buildDummyParticipant(id, generateName(i)));
            }
            ev = { ...ev, participants: base, participantDetails: baseDetails };
          }
        }
        // Also enforce general dev minima for all events
        return ensureDevMinParticipants(ev);
      });
      setEvents(eventData);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  }, [ensureDevMinParticipants, eventsService, isAuthenticated]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents, isAuthenticated]);

  const signUp = async (id: string, userId: string) => {
    if (!user) {
      toast.error(t("auth.required"));
      return;
    }

    console.log('[SupabaseEventProvider] signUp called with userId:', userId, 'user.id:', user.id);

    const event = events.find(e => e.id === id);
    
    if (event) {
      const eventDateTime = new Date(event.date);
      eventDateTime.setHours(parseInt(event.time.split(':')[0]), parseInt(event.time.split(':')[1]));
      const now = new Date();
      const hoursUntilEvent = (eventDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

      // Check if event is in the past
      if (hoursUntilEvent < 0) {
        toast.error(t("event.signup.past_event"));
        return;
      }

      // Check signup window (admins can bypass)
      if (!user.isAdmin && hoursUntilEvent > signupHoursBeforeEvent) {
        toast.error(t("event.signup.too_early", { hours: signupHoursBeforeEvent }));
        return;
      }

      // Check if user has penalties that prevent signup
      const userPenalty = getMemberPenalty(userId);
      
      if (userPenalty) {
        const signupCheck = canSignupToEvent(userPenalty, eventDateTime.toISOString());
        
        if (!signupCheck.canSignup) {
          toast.error(signupCheck.reason || t("event.signup.penalty"));
          return;
        }
      }
    }
    
    if (event && !event.participants.includes(userId)) {
      if (event.participants.length >= event.maxParticipants) {
        toast.error(t("event.signup.full"));
        return;
      }

      // OPTIMISTIC UPDATE: Update UI immediately for instant feedback
      setEvents(prev => prev.map(e => {
        if (e.id !== id) return e;
        const updated = { ...e };
        
        if (!(updated.participants || []).includes(userId)) {
          updated.participants = [...(updated.participants || []), userId];
          const firstName = user.firstName || 'You';
          updated.participantDetails = [...(updated.participantDetails || []), buildDummyParticipant(userId, firstName, user.lastName, user.phone)];
        }

        // If this is a tournament (non-Mexicano), ensure participants are divisible by 4
        if (updated.type === 'tournament' && !isMexicanoEvent(updated)) {
          const current = updated.participants.length;
          const target = nextMultipleOfFour(current);
          let need = target - current;

          // Promote from waiting list first
          while (need > 0 && (updated.waitingList || []).length > 0) {
            const nextId = updated.waitingList!.shift()!;
            const nextDetails = (updated.waitingListDetails || []).find(p => p.id === nextId);
            updated.waitingListDetails = (updated.waitingListDetails || []).filter(p => p.id !== nextId);
            updated.participants.push(nextId);
            updated.participantDetails!.push(nextDetails || buildDummyParticipant(nextId, generateName(current + need)));
            need--;
          }
        }
        return updated;
      }));

      toast.success(t("event.signup.success"));

      // For non-UUID (dev) users, stop here
      if (!isUuid(userId)) {
        return;
      }

      // For real users, persist to database
      try {
        const success = await eventsService.signUpForEvent(id, userId);
        if (!success) {
          // Rollback on failure
          setEvents(prev => prev.map(e => {
            if (e.id !== id) return e;
            return {
              ...e,
              participants: (e.participants || []).filter(p => p !== userId),
              participantDetails: (e.participantDetails || []).filter(p => p.id !== userId)
            };
          }));
          toast.error(t("event.signup.error"));
        }
      } catch (error) {
        // Rollback on error
        setEvents(prev => prev.map(e => {
          if (e.id !== id) return e;
          return {
            ...e,
            participants: (e.participants || []).filter(p => p !== userId),
            participantDetails: (e.participantDetails || []).filter(p => p.id !== userId)
          };
        }));
        toast.error(t("event.signup.error"));
      }
    }
  };

  const cancel = async (id: string, userId: string) => {
    if (!user) {
      toast.error(t("auth.required"));
      return;
    }

    const event = events.find(e => e.id === id);
    if (event) {
      // Check if this is a late cancellation (less than 3 hours before event)
      const eventDateTime = new Date(event.date);
      eventDateTime.setHours(parseInt(event.time.split(':')[0]), parseInt(event.time.split(':')[1]));
      const now = new Date();
      const hoursUntilEvent = (eventDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      // Add penalty for late cancellation if user is a confirmed participant
      if (event.participants.includes(userId) && hoursUntilEvent < 3 && hoursUntilEvent > 0) {
        addPenalty({
          memberId: userId,
          eventId: id,
          eventName: event.title,
          eventDate: event.date.toISOString(),
          type: 'late_cancel',
          reason: 'Cancelled less than 3 hours before event start'
        });
        toast.warning(t("event.cancel.penalty"));
      }
      
      // OPTIMISTIC UPDATE: Update UI immediately
      setEvents(prev => prev.map(e => {
        if (e.id !== id) return e;
        return {
          ...e,
          participants: (e.participants || []).filter(p => p !== userId),
          participantDetails: (e.participantDetails || []).filter(p => p.id !== userId)
        };
      }));
      
      toast.success(t("event.cancel.success"));

      // For non-UUID (dev) users, stop here
      if (!isUuid(userId)) {
        return;
      }

      // For real users, persist to database
      try {
        const success = await eventsService.cancelEventSignup(id, userId);
        if (!success) {
          // Rollback on failure
          const firstName = user.firstName || 'User';
          setEvents(prev => prev.map(e => {
            if (e.id !== id) return e;
            return {
              ...e,
              participants: [...(e.participants || []), userId],
              participantDetails: [...(e.participantDetails || []), buildDummyParticipant(userId, firstName, user.lastName, user.phone)]
            };
          }));
          toast.error(t("event.cancel.error"));
        }
      } catch (error) {
        // Rollback on error
        const firstName = user.firstName || 'User';
        setEvents(prev => prev.map(e => {
          if (e.id !== id) return e;
          return {
            ...e,
            participants: [...(e.participants || []), userId],
            participantDetails: [...(e.participantDetails || []), buildDummyParticipant(userId, firstName, user.lastName, user.phone)]
          };
        }));
        toast.error(t("event.cancel.error"));
      }
    }
  };

  const joinWaitingList = async (id: string, userId: string) => {
    if (!user) {
      toast.error(t("auth.required"));
      return;
    }

    // OPTIMISTIC UPDATE: Update UI immediately
    let position = 1;
    setEvents(prev => prev.map(e => {
      if (e.id !== id) return e;
      const updated = { ...e };
      position = (updated.waitingList?.length || 0) + 1;
      updated.waitingList = [...(updated.waitingList || []), userId];
      const firstName = user.firstName || 'You';
      updated.waitingListDetails = [...(updated.waitingListDetails || []), buildDummyParticipant(userId, firstName, user.lastName, user.phone)];
      return updated;
    }));

    toast.success(t("event.waitlist.joined", { position }));

    // For non-UUID (dev) users, stop here
    if (!isUuid(userId)) {
      return;
    }

    // For real users, persist to database
    try {
      const pos = await eventsService.joinWaitingList(id, userId);
      if (!pos) {
        // Rollback on failure
        setEvents(prev => prev.map(e => {
          if (e.id !== id) return e;
          return {
            ...e,
            waitingList: (e.waitingList || []).filter(p => p !== userId),
            waitingListDetails: (e.waitingListDetails || []).filter(p => p.id !== userId)
          };
        }));
        toast.error(t("event.waitlist.error"));
      }
    } catch (error) {
      // Rollback on error
      setEvents(prev => prev.map(e => {
        if (e.id !== id) return e;
        return {
          ...e,
          waitingList: (e.waitingList || []).filter(p => p !== userId),
          waitingListDetails: (e.waitingListDetails || []).filter(p => p.id !== userId)
        };
      }));
      toast.error(t("event.waitlist.error"));
    }
  };

  const leaveWaitingList = async (id: string, userId: string) => {
    if (!user) {
      toast.error(t("auth.required"));
      return;
    }

    // OPTIMISTIC UPDATE: Update UI immediately
    setEvents(prev => prev.map(e => {
      if (e.id !== id) return e;
      return {
        ...e,
        waitingList: (e.waitingList || []).filter(p => p !== userId),
        waitingListDetails: (e.waitingListDetails || []).filter(p => p.id !== userId)
      };
    }));

    toast.success(t("event.waitlist.left"));

    // For non-UUID (dev) users, stop here
    if (!isUuid(userId)) {
      return;
    }

    // For real users, persist to database
    try {
      const success = await eventsService.leaveWaitingList(id, userId);
      if (!success) {
        // Rollback on failure
        const firstName = user.firstName || 'User';
        setEvents(prev => prev.map(e => {
          if (e.id !== id) return e;
          const updated = { ...e };
          const position = (updated.waitingList?.length || 0) + 1;
          updated.waitingList = [...(updated.waitingList || []), userId];
          updated.waitingListDetails = [...(updated.waitingListDetails || []), buildDummyParticipant(userId, firstName, user.lastName, user.phone)];
          return updated;
        }));
        toast.error(t("event.waitlist.error"));
      }
    } catch (error) {
      // Rollback on error
      const firstName = user.firstName || 'User';
      setEvents(prev => prev.map(e => {
        if (e.id !== id) return e;
        const updated = { ...e };
        const position = (updated.waitingList?.length || 0) + 1;
        updated.waitingList = [...(updated.waitingList || []), userId];
        updated.waitingListDetails = [...(updated.waitingListDetails || []), buildDummyParticipant(userId, firstName, user.lastName, user.phone)];
        return updated;
      }));
      toast.error(t("event.waitlist.error"));
    }
  };

  const removeParticipant = async (id: string, participantId: string) => {
    if (!user?.isAdmin) {
      toast.error(t("admin.required"));
      return;
    }
    if (!isUuid(participantId)) {
      // Local-only removal with promotion from waitlist; then enforce dev minima
      setEvents(prev => prev.map(e => {
        if (e.id !== id) return e;
        let participants = (e.participants || []).filter(p => p !== participantId);
        let participantDetails = (e.participantDetails || []).filter(p => p.id !== participantId);
        const waitingList = [...(e.waitingList || [])];
        let waitingListDetails = [...(e.waitingListDetails || [])];
        if (waitingList.length > 0) {
          const nextId = waitingList.shift()!;
          const nextDetails = waitingListDetails.find(p => p.id === nextId) || buildDummyParticipant(nextId, 'Next Player');
          participants = [...participants, nextId];
          participantDetails = [...participantDetails, nextDetails];
          waitingListDetails = waitingListDetails.filter(p => p.id !== nextId);
        }
        const ensured = ensureDevMinParticipants({ ...e, participants, participantDetails, waitingList, waitingListDetails });
        return ensured;
      }));
      toast.success(t("event.participant.removed"));
      return;
    }
    const success = await eventsService.removeParticipant(id, participantId);
    if (success) {
      toast.success(t("event.participant.removed"));
      await loadEvents();
    } else {
      toast.error(t("event.participant.error"));
    }
  };

  const contextValue = {
    events,
    isLoading,
    signUp,
    cancel,
    joinWaitingList,
    leaveWaitingList,
    removeParticipant,
    refreshEvents: loadEvents,
  };

  return (
    <EventContext.Provider value={contextValue}>
      {children}
    </EventContext.Provider>
  );
}
